import { differenceInCalendarDays, format, isToday, parseISO } from 'date-fns';
import { AlertTriangle, ChevronRight, Clock, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { StatusChip } from '@/modules/hiring-manager/components/StatusChip';
import { Button } from '@/modules/ui/components/button/Button';
import { getInitials } from '@/utils/getInitials';

import {
  candidateName,
  failureReason,
  latestOutbound,
  type MessageApi,
  type MessageThreadApi,
  outboundKindLabel,
  reasonCopy,
} from './outreachThread';

const relativeDay = (value: string | null | undefined) => {
  if (!value) return '';
  const date = parseISO(value);
  if (isToday(date)) return format(date, 'HH:mm');
  const days = differenceInCalendarDays(new Date(), date);
  return days < 7 ? `${days}d` : format(date, 'MMM d');
};

function ThreadStatus({ thread }: { thread: MessageThreadApi }) {
  const { hasReply, latestOutboundStatus, latestOutboundStatusAt } =
    thread.summary;

  if (hasReply) {
    return (
      <span className="text-muted-foreground inline-flex h-5 items-center gap-1.5 text-xs font-medium whitespace-nowrap">
        <span className="bg-semantic-560 size-1.5 rounded-full" />
        Replied
      </span>
    );
  }
  if (latestOutboundStatus === 'Pending') {
    return (
      <span className="inline-flex h-5 items-center gap-1.5 text-xs font-medium whitespace-nowrap text-neutral-500">
        <Clock className="size-3.5" />
        Queued
        {latestOutboundStatusAt &&
          ` · ${format(parseISO(latestOutboundStatusAt), 'EEE')}`}
      </span>
    );
  }
  if (latestOutboundStatus === 'Failed') {
    return <StatusChip tone="failed">Failed</StatusChip>;
  }
  // Only Sent and Read mean the message reached the candidate. A draft or
  // a thread with no outbound message yet must not read as delivered.
  if (latestOutboundStatus === 'Draft') {
    return (
      <span className="inline-flex h-5 items-center text-xs font-medium whitespace-nowrap text-neutral-500">
        Draft
      </span>
    );
  }
  if (!latestOutboundStatus) {
    return (
      <span className="inline-flex h-5 items-center text-xs font-medium whitespace-nowrap text-neutral-500">
        Not sent
      </span>
    );
  }
  return (
    <span className="inline-flex h-5 items-center gap-1.5 text-xs font-medium whitespace-nowrap text-neutral-500">
      <span className="bg-primary-600 size-1.5 rounded-full" />
      Sent
    </span>
  );
}

function ThreadMessages({ thread }: { thread: MessageThreadApi }) {
  const messages = [...(thread.messages ?? [])].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  );

  return (
    <ol className="border-border flex flex-col gap-3 border-l-2 pl-4">
      {messages.map((message) => (
        <li className="flex flex-col gap-1" key={message.id}>
          <span className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
            <span
              className={cn(
                'font-medium',
                message.origin === 'Recruiter'
                  ? 'text-primary-700'
                  : 'text-foreground',
              )}
            >
              {message.origin === 'Recruiter'
                ? 'Finlay'
                : candidateName(thread)}
            </span>
            {format(
              parseISO(message.sentAt ?? message.createdAt),
              'MMM d, HH:mm',
            )}
            {message.status === 'Failed' && (
              <span className="inline-flex items-center gap-1 text-red-700">
                <AlertTriangle className="size-3" />
                {reasonCopy[failureReason(message)].title}
              </span>
            )}
          </span>
          <p className="max-w-[64ch] text-[13px] leading-[18px] whitespace-pre-line">
            {message.content}
          </p>
        </li>
      ))}
    </ol>
  );
}

interface MessageThreadRowProps {
  thread: MessageThreadApi;
  /** Set only when a job is selected, since a thread carries no job. */
  candidateHref?: string;
  isResending: boolean;
  onResend: (message: MessageApi) => void;
}

/**
 * One candidate's outreach: who, what Finlay last sent, and where it stands.
 * Expanding shows every message in full; the text is never editable here.
 */
export function MessageThreadRow({
  thread,
  candidateHref,
  isResending,
  onResend,
}: MessageThreadRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const last = latestOutbound(thread);
  const name = candidateName(thread);
  // LinkedIn's errors and its weekly limit are worth a manual retry (the
  // limit's once the week is up; the API retries neither). An unreachable
  // profile never resolves, and a disconnected session needs the account
  // reconnected first.
  const reason = last?.status === 'Failed' ? failureReason(last) : undefined;
  const canResend = reason === 'unknown' || reason === 'limit';

  return (
    <li className="px-2">
      <div className="group hover:bg-accent -mx-2 grid min-h-12 grid-cols-[16px_24px_minmax(0,1fr)_auto] items-center gap-3 rounded-md px-2 py-1.5 transition-colors duration-100 md:grid-cols-[16px_24px_minmax(0,1fr)_minmax(0,1.6fr)_112px_56px]">
        <button
          aria-expanded={isOpen}
          aria-label={
            isOpen ? `Hide messages to ${name}` : `Show messages to ${name}`
          }
          className="focus-visible:ring-ring/50 grid size-4 cursor-pointer place-items-center rounded text-neutral-500 outline-none hover:text-current focus-visible:ring-[3px]"
          onClick={() => setIsOpen((value) => !value)}
          type="button"
        >
          <ChevronRight
            className={cn('size-4 transition-transform', isOpen && 'rotate-90')}
          />
        </button>
        <span className="bg-secondary-150 text-primary-700 grid size-6 place-items-center rounded-full text-[10px] font-semibold tracking-wide">
          {getInitials(name)}
        </span>
        <span className="flex min-w-0 flex-col gap-px">
          {candidateHref ? (
            <Link
              className="truncate font-medium hover:underline"
              to={candidateHref}
            >
              {name}
            </Link>
          ) : (
            <span className="truncate font-medium">{name}</span>
          )}
          <span className="truncate text-[13px] text-neutral-500">
            {outboundKindLabel(thread)}
          </span>
        </span>
        <span className="text-muted-foreground hidden truncate text-[13px] md:block">
          {last?.content}
        </span>
        <span className="flex justify-end md:justify-start">
          <ThreadStatus thread={thread} />
        </span>
        <span className="hidden text-right text-xs text-neutral-500 tabular-nums md:block">
          {relativeDay(
            thread.summary.latestOutboundStatusAt ?? thread.updatedAt,
          )}
        </span>
      </div>
      {isOpen && (
        <div className="flex flex-col gap-3 py-3 pl-[52px]">
          <ThreadMessages thread={thread} />
          {canResend && last && (
            <Button
              className="h-7 w-fit rounded-md"
              loading={isResending}
              onClick={() => onResend(last)}
              size="sm"
              variant="outline"
            >
              <RefreshCw className="size-3.5" />
              Resend
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
